// 消息流容器 —— 自动跟尾 + 跨日插入 TimeDivider + 同 sender 连续消息 compact

import { useEffect, useRef } from "react";

import { ApprovalCard } from "./ApprovalCard";
import { MessageBubble, type BubbleMessage } from "./MessageBubble";
import { TerminalBlock } from "./TerminalBlock";
import { TimeDivider, sameDay } from "./TimeDivider";

interface MessageListProps {
  messages: BubbleMessage[];
  /** F3b 给 ApprovalCard 用：当前登录用户 id */
  currentUserId?: string | null;
  /** F3b 给 ApprovalCard 用：发裸 ws 文本（approval-response） */
  sendRaw?: (text: string) => void;
}

export function MessageList({ messages, currentUserId = null, sendRaw }: MessageListProps) {
  const listRef = useRef<HTMLOListElement>(null);

  // 简单跟尾：messages 长度变化就 scroll 到底
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight });
  }, [messages.length]);

  if (messages.length === 0) {
    return (
      <ol className="msg-list msg-list--empty" ref={listRef}>
        <li className="msg-list__hint">
          （这里还很安静。打开另一个 tab，URL 加 <code>?device=bob</code> 模拟另一个人。）
        </li>
      </ol>
    );
  }

  const items: React.ReactNode[] = [];
  let lastTs: number | null = null;
  let lastSender: string | null = null;

  for (const m of messages) {
    if (lastTs === null || !sameDay(lastTs, m.ts)) {
      items.push(<TimeDivider key={`d-${m.id}`} ts={m.ts} />);
      lastSender = null;
    }
    if (m.approvalRequest && sendRaw) {
      items.push(
        <li key={m.id} className="msg-list__approval">
          <ApprovalCard
            data={m.approvalRequest}
            currentUserId={currentUserId}
            sendRaw={sendRaw}
          />
        </li>,
      );
      lastTs = m.ts;
      lastSender = null;
      continue;
    }
    if (m.invocationId) {
      const compact = lastSender === m.device && lastTs !== null && m.ts - lastTs < 3 * 60_000;
      items.push(
        <TerminalBlock
          key={m.id}
          adapter={m.device}
          invocationId={m.invocationId}
          text={m.text}
          isComplete={m.isComplete ?? false}
          eofReason={m.eofReason}
          stale={m.stale}
          resizeAck={m.resizeAck}
          ts={m.ts}
          compact={compact}
          onCancel={
            sendRaw
              ? (id) =>
                  sendRaw(
                    JSON.stringify({ kind: "cancel-invocation", invocation_id: id }),
                  )
              : undefined
          }
          onInput={
            sendRaw
              ? (id, data) =>
                  sendRaw(
                    JSON.stringify({
                      kind: "terminal-input",
                      invocation_id: id,
                      data,
                    }),
                  )
              : undefined
          }
          onResize={
            sendRaw
              ? (id, cols, rows) =>
                  sendRaw(
                    JSON.stringify({
                      kind: "terminal-resize",
                      invocation_id: id,
                      cols,
                      rows,
                    }),
                  )
              : undefined
          }
        />,
      );
      lastTs = m.ts;
      lastSender = m.device;
      continue;
    }
    const compact = lastSender === m.device && lastTs !== null && m.ts - lastTs < 3 * 60_000;
    items.push(<MessageBubble key={m.id} msg={m} compact={compact} />);
    lastTs = m.ts;
    lastSender = m.device;
  }

  return (
    <ol className="msg-list" ref={listRef}>
      {items}
    </ol>
  );
}
